/**
 * Blog List Page
 * Displays all blog posts
 */
import { Link } from 'react-router-dom';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography, Card, CardContent, Chip, Stack } from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useEffect } from 'react';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

interface BlogPost {
  slug: string;
  title: string;
  titleEn: string;
  excerpt: string;
  date: string;
  readTime: string;
  language: 'zh-yue' | 'zh-hans' | 'en';
  category: string;
}

const blogPosts: BlogPost[] = [
  // English posts (sorted by date: newest first)
  {
    slug: 'shorter-vs-longer-mortgage-term-hong-kong',
    title: 'Shorter or Longer Mortgage Term in Hong Kong? Monthly Payment vs Total Interest',
    titleEn: 'Shorter or Longer Mortgage Term?',
    excerpt: 'Compare 15, 20, 25 and 30-year terms on a HK$2 million loan and see how term length changes the monthly payment and total interest.',
    date: '2026-10-09',
    readTime: '5 min',
    language: 'en',
    category: 'Mortgage Guide'
  },
  {
    slug: 'how-much-can-you-borrow-hk10000-monthly-mortgage',
    title: 'How Much Can You Borrow with HK$10,000 a Month? Mortgage Affordability Explained',
    titleEn: 'Borrowing Power on HK$10,000 a Month',
    excerpt: 'Work backwards from a monthly payment to a loan amount, see how rate and term change borrowing power, and learn what banks check beyond the formula.',
    date: '2026-10-07',
    readTime: '6 min',
    language: 'en',
    category: 'Mortgage Guide'
  },
  {
    slug: 'hong-kong-mortgage-amortization-explained',
    title: 'Mortgage Amortization Explained: How Your Hong Kong Home Loan Works',
    titleEn: 'Mortgage Amortization Explained',
    excerpt: 'See how monthly mortgage payments split between interest and principal, why the balance falls slowly at first, and how rate or term changes affect repayment.',
    date: '2026-10-06',
    readTime: '6 min',
    language: 'en',
    category: 'Mortgage Guide'
  },
  {
    slug: '5-mortgage-mistakes-first-time-buyers-hong-kong',
    title: '5 Common Mortgage Mistakes First-Time Buyers Make in Hong Kong',
    titleEn: '5 Mortgage Mistakes First-Time Buyers Make',
    excerpt: 'Avoid these 5 costly mortgage mistakes when buying your first home in Hong Kong. Learn about stress tests, fixed rates, bank switching, and hidden fees.',
    date: '2026-05-18',
    readTime: '3 min',
    language: 'en',
    category: 'First-Time Buyer'
  },
  {
    slug: 'emergency-fund-how-much-hong-kong',
    title: 'Emergency Fund: How Much is Enough? A Practical Guide for Hong Kong',
    titleEn: 'Emergency Fund: How Much is Enough?',
    excerpt: 'Learn how much emergency fund to save (3-6 months of living expenses). Practical guide for Hong Kong covering calculation, where to keep it, and real examples.',
    date: '2026-05-10',
    readTime: '3 min',
    language: 'en',
    category: 'Personal Finance'
  },
  {
    slug: 'effective-interest-rate-vs-flat-rate-hong-kong',
    title: 'Understanding Effective Interest Rates vs. Flat Rates in Hong Kong',
    titleEn: 'Effective Interest Rate vs Flat Rate',
    excerpt: 'Learn the crucial difference between effective interest rates (APR) and flat rates in Hong Kong loans. Understand how banks calculate interest, avoid common traps, and make smarter borrowing decisions in 2026.',
    date: '2026-04-21',
    readTime: '12 min',
    language: 'en',
    category: 'Loan Guide'
  },
  {
    slug: 'how-to-read-mortgage-statement-hong-kong',
    title: 'How to Read Your Mortgage Statement in Hong Kong',
    titleEn: 'How to Read Your Mortgage Statement',
    excerpt: 'Understand every number on your Hong Kong mortgage statement. Covers principal, interest breakdown, outstanding balance, penalty clauses, and a monthly checklist to catch costly errors early.',
    date: '2026-04-12',
    readTime: '5 min',
    language: 'en',
    category: 'Mortgage Guide'
  },
  {
    slug: 'hos-mortgage-guide-hong-kong-2026',
    title: 'HOS Mortgage Guide 2026: Complete Calculation & Application Strategy',
    titleEn: 'HOS Mortgage Guide 2026: Complete Guide',
    excerpt: 'Complete HOS mortgage guide 2026. Learn calculation methods, maximum loan amounts, interest rates, application process, and key differences from private property mortgages. Includes real examples and step-by-step strategies.',
    date: '2026-04-10',
    readTime: '16 min',
    language: 'en',
    category: 'HOS & Subsidized Housing'
  },
  {
    slug: 'hong-kong-stamp-duty-guide-2026',
    title: 'Hong Kong Stamp Duty 2026: Complete Guide for Property Buyers',
    titleEn: 'Hong Kong Stamp Duty 2026: Complete Guide',
    excerpt: 'Complete guide to Hong Kong stamp duty 2026. Learn about BSD, AVD, SSD rates for first-time and non-first-time buyers. Includes calculation examples, exemptions, payment process, and strategies to minimize tax burden legally.',
    date: '2026-04-09',
    readTime: '15 min',
    language: 'en',
    category: 'Property Tax Guide'
  },
  {
    slug: 'rent-vs-buy-hong-kong-2026',
    title: 'Rent vs Buy in Hong Kong 2026: Complete Financial Analysis & Decision Guide',
    titleEn: 'Rent vs Buy in Hong Kong 2026: Complete Financial Analysis',
    excerpt: 'Should you rent or buy in Hong Kong? Complete financial analysis comparing costs, opportunity costs, breakeven points, market outlook, and lifestyle factors. Includes real case studies and decision framework.',
    date: '2026-03-22',
    readTime: '18 min',
    language: 'en',
    category: 'Property Investment'
  },
  {
    slug: 'mortgage-refinancing-hong-kong-2026',
    title: 'Mortgage Refinancing in Hong Kong 2026: When and How to Switch Banks',
    titleEn: 'Mortgage Refinancing in Hong Kong 2026: When and How to Switch Banks',
    excerpt: 'Save thousands in interest with strategic refinancing. Complete guide covering when to refinance, cost analysis, step-by-step process, bank comparison, and expert negotiation tips for Hong Kong homeowners.',
    date: '2026-03-15',
    readTime: '8 min',
    language: 'en',
    category: 'Mortgage Strategy'
  },
  {
    slug: 'mpf-maximization-retirement-planning-hong-kong-2026',
    title: 'MPF Maximization and Retirement Planning in Hong Kong 2026',
    titleEn: 'MPF Maximization and Retirement Planning in Hong Kong 2026',
    excerpt: 'Transform your MPF into a powerful retirement engine. Learn how to optimize fund selection, minimize fees, and build a complete 3-pillar retirement strategy. Small changes today can mean millions more at retirement.',
    date: '2026-03-10',
    readTime: '14 min',
    language: 'en',
    category: 'Retirement Planning'
  },
  {
    slug: 'credit-card-debt-management-hong-kong-2026',
    title: 'Credit Card Debt Management and Smart Usage in Hong Kong 2026',
    titleEn: 'Credit Card Debt Management and Smart Usage in Hong Kong 2026',
    excerpt: 'Break free from credit card debt with proven strategies. Learn the 4-step elimination plan, debt consolidation options, and smart credit card usage rules to maximize rewards without the trap.',
    date: '2026-02-25',
    readTime: '12 min',
    language: 'en',
    category: 'Debt Management'
  },
  {
    slug: 'emergency-fund-hong-kong-2026',
    title: 'Emergency Fund Blueprint for Hong Kong in 2026',
    titleEn: 'Emergency Fund Blueprint for Hong Kong in 2026',
    excerpt: 'Learn exactly how much emergency cash you need, where to keep it, and a practical 90-day action plan to build financial resilience in Hong Kong.',
    date: '2026-02-22',
    readTime: '9 min',
    language: 'en',
    category: 'Financial Planning'
  },
  {
    slug: '2026-personal-finance-planning-guide',
    title: 'Personal Finance Planning Guide 2026: Complete Strategy',
    titleEn: 'Personal Finance Planning Guide 2026',
    excerpt: 'Build wealth and achieve financial freedom with proven strategies. Complete guide covering cash flow, emergency funds, debt management, investment planning, and insurance for Hong Kong residents.',
    date: '2026-02-12',
    readTime: '15 min',
    language: 'en',
    category: 'Financial Planning'
  },
  {
    slug: 'best-virtual-banks-hong-kong-2026',
    title: 'Best Virtual Banks in Hong Kong 2026: Complete Comparison Guide',
    titleEn: 'Best Virtual Banks in Hong Kong 2026',
    excerpt: 'All 8 HK virtual banks compared — Mox, ZA Bank, livi, WeLab, Airstar, PAObank, Ant Bank & Fusion Bank. Savings rates, features, security, and our top picks.',
    date: '2026-02-08',
    readTime: '10 min',
    language: 'en',
    category: 'Banking & Savings'
  },
  {
    slug: 'hong-kong-fixed-deposit-rates-comparison-february-2026',
    title: 'Hong Kong Fixed Deposit Interest Rates Comparison February 2026',
    titleEn: 'HK Fixed Deposit Rates Comparison Feb 2026',
    excerpt: 'Complete comparison of major banks\' FD rates (4.0-5.5%), special promotions, and where to get the best returns. HSBC, ZA Bank, Mox, Citibank rates analyzed.',
    date: '2026-02-06',
    readTime: '8 min',
    language: 'en',
    category: 'Banking & Savings'
  },
  {
    slug: '5-essential-financial-planning-steps-hong-kong-2026',
    title: '5 Essential Financial Planning Steps for Hong Kong Residents 2026',
    titleEn: '5 Essential Financial Planning Steps for Hong Kong 2026',
    excerpt: 'Build lasting wealth with this practical roadmap: emergency fund, debt elimination, MPF maximization, insurance protection, and smart investing. Complete guide for Hong Kong residents.',
    date: '2026-02-03',
    readTime: '7 min',
    language: 'en',
    category: 'Financial Planning'
  },
  {
    slug: 'gold-silver-investment-investigation-2026',
    title: 'Gold and Silver Investment Investigation 2026: Safe Haven or Risky Bet?',
    titleEn: 'Gold and Silver Investment Investigation 2026',
    excerpt: 'Critical analysis of precious metals as investments. Discover hidden costs, real returns vs alternatives, and when gold/silver actually make sense for Hong Kong investors.',
    date: '2026-02-02',
    readTime: '5 min',
    language: 'en',
    category: 'Investment Analysis'
  },
  {
    slug: 'fixed-vs-floating-rate-mortgages-hong-kong-2026',
    title: 'Fixed vs. Floating Rate Mortgages: Which Should You Choose in 2026?',
    titleEn: 'Fixed vs. Floating Rate Mortgages Hong Kong 2026',
    excerpt: 'Comprehensive comparison of fixed and floating rate mortgages in Hong Kong. Learn the pros, cons, real cost comparisons, and expert tips to choose the best option for your situation.',
    date: '2026-02-01',
    readTime: '8 min',
    language: 'en',
    category: 'Mortgage Strategy'
  },
  {
    slug: 'true-cost-5-million-mortgage-hong-kong-2026',
    title: 'The True Cost of a HKD 5 Million Mortgage: Beyond the Monthly Payment',
    titleEn: 'The True Cost of a HKD 5 Million Mortgage',
    excerpt: 'Shocking reality: A HKD 5M property costs HKD 7.9M over 25 years! Complete breakdown of interest, stamp duty, fees, and insurance. Learn how to reduce your total cost by hundreds of thousands.',
    date: '2026-01-31',
    readTime: '10 min',
    language: 'en',
    category: 'Mortgage Planning'
  },
  {
    slug: 'first-time-homebuyer-mortgage-guide-2026',
    title: 'First-Time Homebuyer Mortgage Guide: Complete Application Process [2026]',
    titleEn: 'First-Time Homebuyer Mortgage Guide: Complete Application Process [2026]',
    excerpt: 'Essential guide for first-time buyers in Hong Kong. Learn about mortgage application process, required documents, stress tests, government schemes, and common pitfalls to avoid.',
    date: '2026-01-31',
    readTime: '15 min',
    language: 'en',
    category: 'First-Time Buyer'
  },
  {
    slug: 'should-you-make-partial-mortgage-repayments-hk-2026',
    title: 'Should You Make Partial Mortgage Repayments in Hong Kong? [2026 Analysis]',
    titleEn: 'Should You Make Partial Mortgage Repayments in Hong Kong? [2026 Analysis]',
    excerpt: 'Comprehensive 2026 analysis for Hong Kong homeowners: Should you use savings for partial mortgage repayment? Includes real calculations, pros/cons, decision framework, and expert scenarios.',
    date: '2026-01-31',
    readTime: '12 min',
    language: 'en',
    category: 'Mortgage Strategy'
  },
  {
    slug: '2026-hong-kong-best-mortgage-rates',
    title: 'Hong Kong Best Mortgage Rates 2026: Complete Comparison Guide',
    titleEn: 'Hong Kong Best Mortgage Rates 2026: Complete Comparison Guide',
    excerpt: 'Comprehensive comparison of 2026 mortgage rates from major Hong Kong banks. Includes HIBOR vs Prime, fixed vs floating rates, and expert tips to save thousands in interest.',
    date: '2026-01-31',
    readTime: '10 min',
    language: 'en',
    category: 'Mortgage Guide'
  },
  // Cantonese posts (sorted by date: newest first)
  {
    slug: 'shorter-vs-longer-mortgage-term-hong-kong',
    title: '按揭年期揀短定長？月供與總利息的取捨',
    titleEn: 'Shorter or Longer Mortgage Term?',
    excerpt: '以港幣200萬元貸款比較15、20、25及30年期，了解年期如何影響月供與總利息。',
    date: '2026-10-09',
    readTime: '5 分鐘',
    language: 'zh-yue',
    category: '按揭指南'
  },
  {
    slug: 'how-much-can-you-borrow-hk10000-monthly-mortgage',
    title: '月供$10,000可以借幾多？香港按揭借貸能力解析',
    titleEn: 'Borrowing Power on HK$10,000 a Month',
    excerpt: '由每月供款反推貸款額，了解利率與年期如何影響借貸能力，以及銀行還會審查哪些條件。',
    date: '2026-10-07',
    readTime: '6 分鐘',
    language: 'zh-yue',
    category: '按揭指南'
  },
  {
    slug: 'hong-kong-mortgage-amortization-explained',
    title: '一文看懂香港按揭攤還：每月供款如何償還本金與利息',
    titleEn: 'Mortgage Amortization Explained',
    excerpt: '了解每月供款如何分配至利息與本金、為何初期本金下降較慢，以及利率或年期變動如何影響還款。',
    date: '2026-10-06',
    readTime: '6 分鐘',
    language: 'zh-yue',
    category: '按揭指南'
  },
  {
    slug: '5-mortgage-mistakes-first-time-buyers-hong-kong',
    title: '首次買樓5大常見按揭錯誤',
    titleEn: '5 Mortgage Mistakes First-Time Buyers Make',
    excerpt: '避開首次買樓時5個常見按揭錯誤。了解壓力測試、定息按揭、轉按及隱藏收費。',
    date: '2026-05-18',
    readTime: '3 min',
    language: 'zh-yue',
    category: '首次置業'
  },
  {
    slug: 'emergency-fund-how-much-hong-kong',
    title: '緊急儲備金應準備多少？香港理財必讀',
    titleEn: 'Emergency Fund: How Much is Enough?',
    excerpt: '了解應準備 3-6 個月生活費作為應急基金。香港實用指南涵蓋計算方法、存放位置及真實例子。',
    date: '2026-05-10',
    readTime: '3 min',
    language: 'zh-yue',
    category: '個人理財'
  },
  {
    slug: 'effective-interest-rate-vs-flat-rate-hong-kong',
    title: '香港貸款實際年利率 vs 平息：一文看清計算方法與陷阱',
    titleEn: 'Effective Interest Rate vs Flat Rate',
    excerpt: '一文看清香港貸款實際年利率（APR）與平息的分別。了解銀行如何計算利息、避開常見陷阱，在2026年作出更精明的借貸決定。',
    date: '2026-04-21',
    readTime: '12 分鐘',
    language: 'zh-yue',
    category: '貸款指南'
  },
  {
    slug: 'how-to-read-mortgage-statement-hong-kong',
    title: '香港按揭月結單全解讀：每個數字代表甚麼？',
    titleEn: 'How to Read Your Mortgage Statement',
    excerpt: '學識閱讀香港按揭月結單每一行數字。包括本金、利息分拆、未償還結餘、罰息條款，以及每月應該檢查的重點。',
    date: '2026-04-12',
    readTime: '5 分鐘',
    language: 'zh-yue',
    category: '按揭指南'
  },
  {
    slug: 'hos-mortgage-guide-hong-kong-2026',
    title: '2026居屋按揭完整指南：計算方法及申請攻略',
    titleEn: 'HOS Mortgage Guide 2026: Complete Guide',
    excerpt: '2026居屋按揭全面指南。了解計算方法、最高貸款額、利率、申請流程，以及與私樓按揭的關鍵分別。包含實例計算及香港買家逐步攻略。',
    date: '2026-04-10',
    readTime: '16 分鐘',
    language: 'zh-yue',
    category: '居屋及資助房屋'
  },
  {
    slug: 'hong-kong-stamp-duty-guide-2026',
    title: '2026香港印花稅完整指南：買樓必知的稅項全攻略',
    titleEn: 'Hong Kong Stamp Duty 2026: Complete Guide',
    excerpt: '2026香港印花稅全面指南。了解從價印花稅、買家印花稅、額外印花稅的首次及非首次置業稅率。包括計算例子、豁免條件、繳付流程及合法節省稅款策略。',
    date: '2026-04-09',
    readTime: '15 分鐘',
    language: 'zh-yue',
    category: '物業稅務指南'
  },
  {
    slug: 'rent-vs-buy-hong-kong-2026',
    title: '2026香港租樓vs買樓完整財務分析及決策指南',
    titleEn: 'Rent vs Buy in Hong Kong 2026',
    excerpt: '香港應該租樓定買樓？完整財務分析比較總成本、機會成本、回本期、市場前景及生活因素。包含真實案例及決策框架，助你作出最佳選擇。',
    date: '2026-03-22',
    readTime: '18 分鐘',
    language: 'zh-yue',
    category: '物業投資'
  },
  {
    slug: 'mortgage-refinancing-hong-kong-2026',
    title: '2026香港按揭轉按全攻略：何時及如何轉換銀行',
    titleEn: 'Mortgage Refinancing in Hong Kong 2026: When and How to Switch Banks',
    excerpt: '透過策略性轉按節省數萬利息開支。完整指南涵蓋轉按時機、成本分析、逐步流程、銀行比較及專家協商技巧，助香港業主獲取最優惠條款。',
    date: '2026-03-15',
    readTime: '8 分鐘',
    language: 'zh-yue',
    category: '按揭策略'
  },
  {
    slug: 'mpf-maximization-retirement-planning-hong-kong-2026',
    title: '2026香港強積金優化及退休規劃完全指南',
    titleEn: 'MPF Maximization and Retirement Planning in Hong Kong 2026',
    excerpt: '將強積金變成強大的退休資產。學習點樣優化基金選擇、減低收費、同建立完整三大支柱退休策略。今日嘅小改變，可以為退休帶來幾百萬額外收入。',
    date: '2026-03-10',
    readTime: '14 分鐘',
    language: 'zh-yue',
    category: '退休規劃'
  },
  {
    slug: 'credit-card-debt-management-hong-kong-2026',
    title: '2026香港信用卡債務管理及精明使用策略',
    titleEn: 'Credit Card Debt Management and Smart Usage in Hong Kong 2026',
    excerpt: '擺脫卡數困境，善用信用卡優惠而不墮入debt陷阱。4步還債策略、債務整合選項、精明使用法則，助你重獲財務自由。',
    date: '2026-02-25',
    readTime: '12 分鐘',
    language: 'zh-yue',
    category: '債務管理'
  },
  {
    slug: 'emergency-fund-hong-kong-2026',
    title: '2026香港應急基金藍圖：幾多先夠、放邊度、點樣開始',
    titleEn: 'Emergency Fund Blueprint for Hong Kong in 2026',
    excerpt: '應急基金要儲幾多？應該放邊度？本文提供清晰公式、存放策略同90日實行計劃，幫你建立真正可用嘅財務安全網。',
    date: '2026-02-22',
    readTime: '9 分鐘',
    language: 'zh-yue',
    category: '理財規劃'
  },
  {
    slug: '2026-personal-finance-planning-guide',
    title: '2026年香港個人理財規劃完整指南',
    titleEn: 'Personal Finance Planning Guide 2026',
    excerpt: '建立財富，實現財務自由的完整策略。涵蓋現金流管理、應急基金、債務管理、投資規劃及保險的香港居民完整指南。',
    date: '2026-02-12',
    readTime: '15 分鐘',
    language: 'zh-yue',
    category: '理財規劃'
  },
  {
    slug: 'best-virtual-banks-hong-kong-2026',
    title: '香港虛擬銀行大比拼 2026：完整比較指南',
    titleEn: 'Best Virtual Banks in Hong Kong 2026',
    excerpt: '八間香港虛擬銀行全面比較 — Mox、ZA Bank、livi、WeLab、天星、PAObank、螞蟻銀行同富融銀行。儲蓄利率、功能、安全性及我哋嘅推薦。',
    date: '2026-02-08',
    readTime: '10 分鐘',
    language: 'zh-yue',
    category: '銀行儲蓄'
  },
  {
    slug: 'hong-kong-fixed-deposit-rates-comparison-february-2026',
    title: '香港定期存款利率比較 2026年2月',
    titleEn: 'HK Fixed Deposit Rates Feb 2026',
    excerpt: '主要銀行定存利率全面比較（4.0-5.5%）、特別推廣及最佳回報分析。滙豐、ZA Bank、Mox、花旗銀行利率詳解。',
    date: '2026-02-06',
    readTime: '8 分鐘',
    language: 'zh-yue',
    category: '銀行儲蓄'
  },
  {
    slug: '5-essential-financial-planning-steps-hong-kong-2026',
    title: '香港居民必備的5個理財規劃步驟 2026',
    titleEn: '5 Essential Financial Planning Steps HK 2026',
    excerpt: '用呢個實用路線圖建立持久財富：應急基金、清還債務、強積金最大化、保險保障、聰明投資。香港居民完整指南。',
    date: '2026-02-03',
    readTime: '7 分鐘',
    language: 'zh-yue',
    category: '理財規劃'
  },
  {
    slug: 'gold-silver-investment-investigation-2026',
    title: '黃金白銀投資調查 2026：避險資產定高風險投資？',
    titleEn: 'Gold and Silver Investment Investigation 2026',
    excerpt: '深入分析貴金屬投資。揭示隱藏成本、實際回報vs其他選項，以及黃金白銀幾時先真係適合香港投資者。',
    date: '2026-02-02',
    readTime: '5 分鐘',
    language: 'zh-yue',
    category: '投資分析'
  },
  {
    slug: 'fixed-vs-floating-rate-mortgages-hong-kong-2026',
    title: '定息按揭 vs 浮息按揭：2026年應該點樣揀？',
    titleEn: 'Fixed vs. Floating Rate Mortgages 2026',
    excerpt: '全面比較香港定息同浮息按揭。詳細分析優缺點、實際成本比較、專家建議，幫你揀到最適合自己嘅按揭方案。',
    date: '2026-02-01',
    readTime: '8 分鐘',
    language: 'zh-yue',
    category: '按揭策略'
  },
  {
    slug: 'true-cost-5-million-mortgage-hong-kong-2026',
    title: '500萬港元按揭的真實成本：不只是每月供款',
    titleEn: 'True Cost of HKD 5M Mortgage',
    excerpt: '震撼現實：500萬物業25年內總支出790萬！完整拆解利息、印花稅、費用及保險。學習如何減少數十萬總成本。',
    date: '2026-01-31',
    readTime: '10 分鐘',
    language: 'zh-yue',
    category: '按揭規劃'
  },
  {
    slug: 'first-time-homebuyer-mortgage-guide-2026',
    title: '首次置業按揭全攻略：從申請到批核完整流程 [2026版]',
    titleEn: 'First-Time Homebuyer Guide 2026',
    excerpt: '首次買樓必讀！詳細講解按揭申請流程、所需文件、壓力測試、政府資助計劃等。幫助新手買家順利獲得按揭批核，實現置業夢想。',
    date: '2026-01-31',
    readTime: '15 分鐘',
    language: 'zh-yue',
    category: '首次置業'
  },
  {
    slug: 'should-you-make-partial-mortgage-repayments-hk-2026',
    title: '2026年應否提早還按揭？香港業主完整分析',
    titleEn: 'Should You Make Partial Repayments?',
    excerpt: '深入分析在2026年加息環境下，香港業主應否使用儲蓄提早還款。包括真實計算例子、利弊分析、決策框架及專家建議。',
    date: '2026-01-31',
    readTime: '12 分鐘',
    language: 'zh-yue',
    category: '按揭策略'
  },
  {
    slug: '2026-hong-kong-best-mortgage-rates',
    title: '2026年香港最優惠按揭利率比較及選擇指南',
    titleEn: 'Hong Kong Best Mortgage Rates 2026',
    excerpt: '全面比較香港主要銀行2026年按揭利率，包括H按、P按、定息、浮息等選擇。助你找到最適合的按揭方案，節省數十萬利息支出。',
    date: '2026-01-31',
    readTime: '10 分鐘',
    language: 'zh-yue',
    category: '按揭指南'
  }
];

export default function Blog() {
  const { i18n, t } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/blog', 'Blog');
    document.title = `${t('home.blog.title')} | PayWise365`;
  }, [t]);

  const seoTitle = isEnglish
    ? 'Hong Kong Mortgage & Finance Blog 2026 | Expert Tips & Guides | PayWise365'
    : '香港按揭理財資訊 2026 | 專業貼士及指南 | PayWise365';

  const seoDescription = isEnglish
    ? 'Expert mortgage and finance articles for Hong Kong. Latest mortgage rates, refinancing tips, first-time buyer guides, retirement planning, and financial strategies. Updated regularly with 2026 market insights.'
    : '香港按揭及理財專業文章。最新按揭利率、轉按貼士、首次置業指南、退休規劃及理財策略。定期更新2026年市場見解。';

  const seoKeywords = isEnglish
    ? 'Hong Kong mortgage blog, finance tips HK, mortgage rates 2026, refinancing guide, first-time buyer, retirement planning, financial planning Hong Kong'
    : '香港按揭資訊, 理財貼士, 2026按揭利率, 轉按指南, 首次置業, 退休規劃, 香港理財規劃';

  // Filter posts by current language
  const currentLanguage = i18n.language;
  const filteredPosts = blogPosts.filter(post => post.language === currentLanguage);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/blog"
        ogType="website"
      />
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {currentLanguage === 'en' ? 'Mortgage Finance Blog' : '按揭理財資訊'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            {currentLanguage === 'en'
              ? 'Expert mortgage insights, latest rate analysis, and financial tips'
              : '專業按揭知識、最新利率分析、理財貼士'}
          </Typography>
        </Container>
      </Box>

      {/* Blog Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={3}>
          {filteredPosts.map((post) => (
            <Card
              key={post.slug}
              component={Link}
              to={`/blog/${post.slug}`}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  borderColor: 'primary.main',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Chip
                    label={post.category}
                    size="small"
                    sx={{
                      bgcolor: 'primary.light',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {post.date}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {post.readTime}
                  </Typography>
                </Stack>

                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600, lineHeight: 1.3 }}>
                  {post.title}
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  {post.excerpt}
                </Typography>

                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  <Typography variant="body2">
                    {currentLanguage === 'en' ? 'Read More' : '閱讀全文'}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {filteredPosts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              {currentLanguage === 'en' ? 'More articles coming soon...' : '更多文章即將發布'}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
